import { EventHookManager, INotifyPropertyChanged } from "../../Utilities";
import { Mutex } from "../../Utilities/Mutext";
import { GetCorrelation, ICorrelation } from "../../ViewModel/Utilities";
import { GetAuthServices } from "../AuthService/AuthService";

export interface ICacheableItem extends INotifyPropertyChanged {
    id: string;
    _PartitionKey: string | undefined;
    _writeVersion: number | undefined;
    Serialize?: () => any;
}

export interface ICacheUpdateArg {
    id: string;
    item?: ICacheableItem;
    type: 'upsert' | 'delete';
}

const doLogging = false;
export class BaseCache<T extends ICacheableItem> {

    constructor(
        {
            doReadFN,
            doWriteFN,
            doDeleteFN,
            debugName,
            syncExternalChanges,
            updateVersionOnWrite,
        }:
            {
                doReadFN: (id: string) => Promise<ICacheableItem>,
                doWriteFN: (data: ICacheableItem) => Promise<any>,
                doDeleteFN: (id: string) => Promise<void>,
                debugName?: string,
                skipCache: boolean,
                syncExternalChanges?: () => Promise<void>,
                updateVersionOnWrite?: boolean
            }
    ) {
        this._readData = doReadFN;
        this._writeData = doWriteFN;
        this._deleteData = doDeleteFN;
        this._debugName = debugName ?? "BaseCache";
        this._syncExternalChanges = syncExternalChanges;
        this._updateVersionOnWrite = updateVersionOnWrite ?? false;

        GetAuthServices().then(async (authServices) => {
            authServices.userChanged.Subscribe(() => {
                if (!authServices.IsLoggedIn())
                    this.ClearCache();
            });
        });
    }

    public get CacheChanged() { return this._cacheUpdateHook.eventHook; }


    public ReadData = async (id: string, _correlation?: ICorrelation): Promise<any> => {
        var correlation = _correlation ?? GetCorrelation();

        return await this._cacheMutex.lock(
            async () => {
                await this.InitializeCache();
                //
                // Check the cache first, if the item is not in the cache, read it from the data store.
                //
                if (!this.HasCachedItem(correlation, id)) {

                    // Log the cache miss
                    //
                    doLogging && console.log(`${this._debugName}:${this._derivedType} | Cache Miss | ${id}`);

                    // do the real read
                    //
                    var result = await this._readData(id);

                    // If the result is null, return null.
                    //
                    if (!result) {
                        return null;
                    }

                    // Serialize the result and store it in the cache.
                    //
                    this.SetCacheItem(correlation, id, result);

                }
                var cacheItem = this.GetCacheItem(correlation, id);
                return cacheItem;
            },
            {
                operation: `ReadData(${id})`,
                correlation: correlation
            }
        );
    }

    /**
     * Writes the data to the data store and the cache.
     * @param data The data to write.
     * @returns A promise that resolves to the result of the write operation which can be .
     * @remarks If the cache is disabled, the data is written to the data store only.
     */
    public WriteData = async (data: T, _correlation?: ICorrelation): Promise<void> => {
        var correlation = _correlation ?? GetCorrelation();

        await this._cacheMutex.lock(
            async () => {

                await this.InitializeCache();
                doLogging && console.log(`[${this._debugName}]:${this._derivedType} | Writing | ${data.id}`);

                //
                // Bump the version if required.
                //
                if (this._updateVersionOnWrite)
                    data._writeVersion = data._writeVersion ? data._writeVersion + 1 : 1;

                // Cache the item
                //
                this.SetCacheItem(correlation, data.id, data);


                // Asyncronously write the data to the data store.
                //
                this._writeData(data);

                //
                // Make sure the cache is up to date
                //
                data.PropertyChanged.Subscribe((e) => {
                    if (e.property === 'id')
                        throw new Error("Cannot update a cached items id.  Please delete and create a new item.");
                });

                // Notify the cache changed event.
                //
                this._cacheUpdateHook.Fire({ id: data.id, item: data, type: 'upsert' });
            },
            {
                operation: `WriteData(${data.id})`,
                correlation: correlation
            }
        );
    }


    /*
        * Writes the data to the data store and the cache.  Used for inititaling/updating the cache with 
        * external changes.
        * 
        * @param data The data to write.
        * @returns A promise that resolves to the result of the write operation which can be .
        * @remarks If the cache is disabled, the data is written to the data store only.
        */
    public UnsafeSetDataArray = async (data: T[]): Promise<T[]> => {
        this._isUnsafeSet = true;
        var cor = GetCorrelation();
        var changedItems: T[] = [];
        data.forEach(datum => {

            let cachedItem = this.GetCacheItem(cor, datum.id);
            let cacheVersion = cachedItem?._writeVersion ?? -1;
            let datumVersion = datum._writeVersion ?? -1;

            if (!cachedItem || datumVersion > cacheVersion) {
                changedItems.push(datum);
                this.SetCacheItem(GetCorrelation(), datum.id, datum)
            }
            else
                console.log(`[${this._debugName}]:${this._derivedType} | Skipping Write | ${datum.id} | Cache Version: ${cacheVersion} | Datum Version: ${datumVersion}`);
        });
        this._isUnsafeSet = false;
        return changedItems;
    }


    public DeleteData = async (id: string, _correlation?: ICorrelation): Promise<any> => {
        var correlation = _correlation ?? GetCorrelation();

        return await this._cacheMutex.lock(
            async () => {
                await this.InitializeCache();
                await this._deleteData(id);

                await this.DeleteCacheItem(correlation, id);

                this._cacheUpdateHook.Fire({ id: id, item: undefined, type: 'delete' });
            },
            {
                operation: `DeleteData(${id})`,
                correlation: correlation
            }
        );
    }

    /**
     * Clears the cache.
     * @remarks This method needs to be overriden by the derived class.
     */
    public ClearCache() {
        this._isInitialized = false;
        throw new Error("Method needs to be overriden by derived class.");
    }


    public HasCachedItem(_correlation: ICorrelation, _id: string): boolean {
        throw new Error("Method needs to be overriden by derived class.");
    }










    public async InitializeCache(shouldAwait = false) {
        if (!this._isInitialized) {
            this._isInitialized = true;

            this.RestoreCache();

            // Running the sync asynchrounously to avoid blocking the main thread.
            //
            if (this._syncExternalChanges) {

                let promise = this._syncExternalChanges();
                if(shouldAwait)
                    await promise;
            }
        }
    }









    /**
     * Restores the cache from the persisted storage.
     * @remarks This method needs to be overriden by the derived class.
     * 
     */
    protected RestoreCache(): void {
        throw new Error("Method needs to be overriden by derived class.");
    }


    protected GetCacheItem(_correlation: ICorrelation, _id: string): T | undefined {
        throw new Error("Method needs to be overriden by derived class.");
    }

    protected SetCacheItem(_correlation: ICorrelation, _id: string, _item: T): void {
        throw new Error("Method needs to be overriden by derived class.");
    }

    protected DeleteCacheItem(_correlation: ICorrelation, _id: string, _skipFlush: boolean = false): void {
        throw new Error("Method needs to be overriden by derived class.");
    }

    protected FlushCache(): void {
        throw new Error("Method needs to be overriden by derived class.");
    }

    protected _derivedType: string = "BaseCache";
    protected _isUnsafeSet = false;
    // protected _cacheGuard = createPromiseGuard("baseCacheGuard");
    private _isInitialized: boolean = false;
    private _cacheUpdateHook = new EventHookManager<ICacheUpdateArg>();
    protected _debugName: string;
    private _updateVersionOnWrite: boolean;
    private _readData: (id: string) => Promise<any>;
    private _writeData: (data: T) => Promise<any>;
    private _deleteData: (id: string) => Promise<void>;
    private _syncExternalChanges?: () => Promise<void>;
    private _cacheMutex = new Mutex('BaseCache');
}